export const stableCaseImageIndex = (caseId: string, imageCount: number) => {
  if (imageCount <= 1) {
    return 0;
  }

  let hash = 2_166_136_261;
  for (let index = 0; index < caseId.length; index += 1) {
    hash ^= caseId.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }

  return (hash >>> 0) % imageCount;
};

export type CaseImageCandidate = {
  id: string;
  group: string;
  imageCount: number;
};

export const stableCaseImageAssignments = (cases: CaseImageCandidate[]) => {
  const assignments = new Map<string, number>();
  const usedByGroup = new Map<string, Set<number>>();

  [...cases]
    .sort((first, second) => first.id.localeCompare(second.id))
    .forEach(({ id, group, imageCount }) => {
      const safeImageCount = Math.max(1, imageCount);
      const preferredIndex = stableCaseImageIndex(id, safeImageCount);
      const usedIndexes = usedByGroup.get(group) ?? new Set<number>();
      let assignedIndex = preferredIndex;

      if (usedIndexes.size < safeImageCount) {
        for (let offset = 0; offset < safeImageCount; offset += 1) {
          const candidateIndex = (preferredIndex + offset) % safeImageCount;
          if (!usedIndexes.has(candidateIndex)) {
            assignedIndex = candidateIndex;
            break;
          }
        }
      }

      assignments.set(id, assignedIndex);
      usedIndexes.add(assignedIndex);
      usedByGroup.set(group, usedIndexes);
    });

  return assignments;
};

export type CaseImageSourceCandidate = {
  id: string;
  createdAt: string;
  group: string;
  legacyGroup: string;
};

export type CaseImageSourcePools = {
  all: readonly string[];
  preferred: Readonly<Record<string, readonly string[]>>;
  legacy: Readonly<Record<string, readonly string[]>>;
};

const chronologicalCaseOrder = (first: CaseImageSourceCandidate, second: CaseImageSourceCandidate) =>
  first.createdAt.localeCompare(second.createdAt) || first.id.localeCompare(second.id);

const firstUnusedSource = (caseId: string, images: readonly string[], usedSources: Set<string>) => {
  if (images.length === 0) {
    return undefined;
  }

  const preferredIndex = stableCaseImageIndex(caseId, images.length);
  for (let offset = 0; offset < images.length; offset += 1) {
    const source = images[(preferredIndex + offset) % images.length];
    if (!usedSources.has(source)) {
      return source;
    }
  }

  return undefined;
};

export const stableCaseImageSources = (
  cases: CaseImageSourceCandidate[],
  pools: CaseImageSourcePools,
  legacyCaseCount = 2
) => {
  const assignments = new Map<string, string>();
  const usedSources = new Set<string>();
  const orderedCases = [...cases].sort(chronologicalCaseOrder);
  const legacyCases = orderedCases.slice(0, legacyCaseCount);
  const legacyIndexes = stableCaseImageAssignments(
    legacyCases.map((caseItem) => ({
      id: caseItem.id,
      group: caseItem.legacyGroup,
      imageCount: Math.max(1, pools.legacy[caseItem.legacyGroup]?.length ?? 0)
    }))
  );

  legacyCases.forEach((caseItem) => {
    const legacyPool = pools.legacy[caseItem.legacyGroup] ?? pools.all;
    if (legacyPool.length === 0) {
      return;
    }

    const source = legacyPool[(legacyIndexes.get(caseItem.id) ?? 0) % legacyPool.length];
    assignments.set(caseItem.id, source);
    usedSources.add(source);
  });

  orderedCases.slice(legacyCaseCount).forEach((caseItem) => {
    const preferredPool = pools.preferred[caseItem.group] ?? pools.all;
    const source =
      firstUnusedSource(caseItem.id, preferredPool, usedSources) ??
      firstUnusedSource(caseItem.id, pools.all, usedSources) ??
      preferredPool[stableCaseImageIndex(caseItem.id, preferredPool.length)] ??
      pools.all[stableCaseImageIndex(caseItem.id, pools.all.length)];

    if (source) {
      assignments.set(caseItem.id, source);
      usedSources.add(source);
    }
  });

  return assignments;
};
